import { Microphone, MicrophoneOff } from '@carbon/icons-react';
import { Alert, Box } from '@mui/material';
import { useEffect } from 'react';

import { useMediaDevicesContext } from '../../providers/media-devices-context';
import ZaraAvatar from '../zara-avatar/zara-avatar';

type MicrophoneTesterProps = {
  noiseThreshold: number;
  size?: number;
  pointSize?: number;
  onMicrophoneReady?: (ready: boolean) => void;
};

export const MicrophoneTester = ({
  noiseThreshold,
  size = 200,
  pointSize = 2,
  onMicrophoneReady,
}: MicrophoneTesterProps) => {
  const { analyser, isAudioEnabled, selectedMicrophone } =
    useMediaDevicesContext();
  const enabled = isAudioEnabled && selectedMicrophone;

  // Reset the ready state when the microphone is changed
  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedMicrophone change must clear readiness
  useEffect(() => {
    onMicrophoneReady?.(false);
  }, [selectedMicrophone, onMicrophoneReady]);

  useEffect(() => {
    if (!selectedMicrophone || !analyser) {
      return;
    }

    // Check if the microphone is working by checking if the audio is above a
    // certain threshold. Only enable the Next button when the audio is above
    // the threshold.
    const sampleBuffer = new Float32Array(analyser.frequencyBinCount);
    let intervalId: NodeJS.Timeout | undefined = setInterval(() => {
      analyser.getFloatFrequencyData(sampleBuffer);
      for (let i = 0; i < sampleBuffer.length; i++) {
        if (sampleBuffer[i] > noiseThreshold) {
          clearInterval(intervalId);
          intervalId = undefined;
          onMicrophoneReady?.(true);
          break;
        }
      }
    }, 100);

    return () => intervalId && clearInterval(intervalId);
  }, [analyser, selectedMicrophone, noiseThreshold, onMicrophoneReady]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          padding: 2,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          height: 240,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: `${size}px`,
              height: `${size}px`,
              position: 'relative',
            }}
          >
            <ZaraAvatar
              isAiSpeaking={false}
              avServiceAnalyser={analyser}
              isLoading={false}
              size={size}
              pointSize={pointSize}
            />
          </Box>
        </Box>
      </Box>

      {enabled ? (
        <Alert
          severity='info'
          icon={<Microphone size={22} />}
          sx={{ textAlign: 'center' }}
        >
          Say &quot;Hi Zara!&quot; to make sure Zara can hear you
        </Alert>
      ) : (
        <Alert
          severity='error'
          icon={<MicrophoneOff size={22} />}
          sx={{ textAlign: 'center' }}
        >
          Audio not enabled
        </Alert>
      )}
    </Box>
  );
};
